import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, TestTube, Loader2, Webhook } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

const EVENT_OPTION_KEYS = [
  { value: "customer.created", labelKey: "settings.webhooks.eventCustomerCreated" },
  { value: "customer.updated", labelKey: "settings.webhooks.eventCustomerUpdated" },
  { value: "booking.created", labelKey: "settings.webhooks.eventBookingCreated" },
  { value: "booking.cancelled", labelKey: "settings.webhooks.eventBookingCancelled" },
  { value: "booking.completed", labelKey: "settings.webhooks.eventBookingCompleted" },
  { value: "payment.completed", labelKey: "settings.webhooks.eventPaymentCompleted" },
  { value: "membership.created", labelKey: "settings.webhooks.eventMembershipCreated" },
  { value: "membership.cancelled", labelKey: "settings.webhooks.eventMembershipCancelled" },
];

export default function SettingsWebhooks() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const qc = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [events, setEvents] = useState<string[]>([]);
  const [active, setActive] = useState(true);

  const { data: webhooks = [] } = useQuery({
    queryKey: ["org-webhooks", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("org_webhooks")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const resetForm = () => {
    setEditId(null); setName(""); setUrl(""); setSecret(""); setEvents([]); setActive(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!name.trim() || !url.trim() || events.length === 0) throw new Error(t("settings.webhooks.errorFillAllFields"));
      const payload = {
        organization_id: organization!.id,
        name: name.trim(),
        url: url.trim(),
        secret: secret.trim() || null,
        events,
        is_active: active,
      };
      if (editId) {
        const { error } = await supabase.from("org_webhooks").update(payload).eq("id", editId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("org_webhooks").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-webhooks"] });
      setShowDialog(false); resetForm();
      toast.success(editId ? t("settings.webhooks.successUpdated") : t("settings.webhooks.successCreated"));
    },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("org_webhooks").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-webhooks"] });
      toast.success(t("admin:toast.webhookDeleted"));
    },
  });

  const testMutation = useMutation({
    mutationFn: async (hookId: string) => {
      await supabase.functions.invoke("fire-webhooks", {
        body: {
          event: "test",
          org_id: organization!.id,
          payload: { message: "Test webhook from admin panel", timestamp: new Date().toISOString() },
        },
      });
    },
    onSuccess: () => toast.success(t("admin:toast.testPayloadSent")),
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("settings.webhooks.errorTestFailed")); },
  });

  const toggleEvent = (value: string) => {
    setEvents((prev) => prev.includes(value) ? prev.filter((e) => e !== value) : [...prev, value]);
  };

  const openEdit = (hook: any) => {
    setEditId(hook.id); setName(hook.name); setUrl(hook.url);
    setSecret(hook.secret ?? ""); setEvents(hook.events ?? []); setActive(hook.is_active);
    setShowDialog(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-1">{t("settings.webhooks.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("settings.webhooks.description")}</p>
        </div>
        <Button size="sm" onClick={() => { resetForm(); setShowDialog(true); }}>
          <Plus className="h-4 w-4 mr-1" /> {t("settings.webhooks.btnAdd")}
        </Button>
      </div>

      {webhooks.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Webhook className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">{t("settings.webhooks.emptyState")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {webhooks.map((hook: any) => (
            <Card key={hook.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{hook.name}</span>
                      <Badge variant={hook.is_active ? "default" : "secondary"} className="text-[10px]">
                        {hook.is_active ? t("settings.webhooks.active") : t("settings.webhooks.inactive")}
                      </Badge>
                      {hook.last_status != null && (
                        <Badge variant={hook.last_status >= 200 && hook.last_status < 300 ? "outline" : "destructive"} className="text-[10px]">
                          {hook.last_status}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{hook.url}</p>
                    <div className="flex gap-1 flex-wrap">
                      {(hook.events ?? []).map((e: string) => (
                        <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>
                      ))}
                    </div>
                    {hook.last_triggered_at && (
                      <p className="text-[10px] text-muted-foreground">
                        {t("settings.webhooks.lastTriggered")}: {format(new Date(hook.last_triggered_at), "d MMM HH:mm", { locale: sv })}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => testMutation.mutate(hook.id)} title={t("settings.webhooks.btnTest")}>
                      <TestTube className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(hook)} title={t("settings.webhooks.btnEdit")}>
                      <Webhook className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteMutation.mutate(hook.id)} title={t("settings.webhooks.btnDelete")}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? t("settings.webhooks.dialogEditTitle") : t("settings.webhooks.dialogCreateTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("settings.webhooks.fieldName")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("settings.webhooks.fieldNamePlaceholder")} className="text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.webhooks.fieldUrl")}</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.zapier.com/..." className="text-sm font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.webhooks.fieldSecret")}</Label>
              <Input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={t("settings.webhooks.fieldSecretPlaceholder")} className="text-sm font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.webhooks.fieldEvents")}</Label>
              <div className="grid grid-cols-2 gap-2">
                {EVENT_OPTION_KEYS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={events.includes(opt.value)} onCheckedChange={() => toggleEvent(opt.value)} />
                    {t(opt.labelKey)}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>{t("settings.webhooks.fieldActive")}</Label>
              <Switch checked={active} onCheckedChange={setActive} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t("settings.webhooks.btnCancel")}</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editId ? t("settings.webhooks.btnSave") : t("settings.webhooks.btnCreate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
